import { createContext, lazy, Suspense, useCallback, useContext, useMemo, useState } from "react";
import type { DiscussionQuestions } from "./DiscussionQuestionsSheet";

// The sheet renders Markdown, the heaviest dependency on the site, and the
// provider wraps every page — the login and invite pages included. So the
// sheet loads the first time it's presented and stays mounted after that,
// which keeps its close animation. Its opening animation is CSS
// @starting-style, so mounting it already open still animates.
const DiscussionQuestionsSheet = lazy(() => import("./DiscussionQuestionsSheet"));

// Companion to SongPlatformsProvider: stands in for the app's Navigator, where
// FeaturedServiceView calls navigator.present(sheet: .discussionQuestions(...)).
//
// Mounted once at the root so any card can present the sheet without each page
// wiring up its own state.

const DiscussionQuestionsContext =
  createContext<((questions: DiscussionQuestions) => void) | null>(null);

export default function DiscussionQuestionsProvider(props: { children: React.ReactNode }) {
  const [questions, setQuestions] = useState<DiscussionQuestions | null>(null);
  const [presented, setPresented] = useState(false);

  const present = useCallback((next: DiscussionQuestions) => {
    setPresented(true);
    setQuestions(next);
  }, []);
  const dismiss = useCallback(() => setQuestions(null), []);
  const value = useMemo(() => present, [present]);

  return <DiscussionQuestionsContext.Provider value={value}>
    {props.children}
    {presented && (
      <Suspense>
        <DiscussionQuestionsSheet questions={questions} onClose={dismiss} />
      </Suspense>
    )}
  </DiscussionQuestionsContext.Provider>
}

/**
 * Presents the discussion questions sheet.
 *
 * Returns null outside a provider so callers can fall back to their own
 * handler rather than crashing — the component gallery relies on that.
 */
export function useDiscussionQuestions(): ((questions: DiscussionQuestions) => void) | null {
  return useContext(DiscussionQuestionsContext);
}
